import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/i18n';
import { ROUTE_DEFINITIONS } from '@/routes';
import { colors } from '@/assets/styles/colors';
import { useUI } from '@/hooks/ui/useUI';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { useScheduleExport } from '@/hooks/schedule/useScheduleExport';
import { scheduleService } from '@/api/domains/schedule';
import { timezoneCatalogService } from '@/api/domains/catalog/timezones';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';
import type { UIExportFormat } from '@/types/fileExport.types';
import type { Schedule } from '@/api/domains/schedule';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';

// ── Filter state type ─────────────────────────────────────────────────────
export interface ScheduleFilters {
  timeZoneId: number | null;
  isTwoDaySchedule: boolean | null;
}

// ── Return type interface ──────────────────────────────────────────────────
export interface UseScheduleListResult {
  rows: Schedule[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  columns: TableColumn<Schedule>[];
  actions: TableAction<Schedule>[];
  onSearch: (term: string) => void;
  onEnterSearch: () => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  isLoading: boolean;
  isExporting: boolean;
  onExport: (format: UIExportFormat) => Promise<void>;
  filters: ScheduleFilters;
  applyFilters: (updater: (previous: ScheduleFilters) => ScheduleFilters) => void;
  timezoneOptions: { value: string; label: string }[];
}


// ── Hook ───────────────────────────────────────────────────────────────────
export function useScheduleList(): UseScheduleListResult {
  const { t: translate } = useTranslation();
  const { showNotification, showApiError, openModal } = useUI();
  const { canEdit, canDelete } = usePagePermissions();
  const navigate = useNavigate();

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<ScheduleFilters>,
      _signal: AbortSignal,
    ): Promise<PaginatedFetchResult<Schedule> | null> => {
      const response = await scheduleService.list({
        page: params.page,
        pageSize: params.pageSize,
        searchText: params.searchText,
        orderBy: params.sort?.key,
        orderDirection: params.sort?.key ? params.sort.direction : undefined,
        timeZoneId: params.filters.timeZoneId,
        isTwoDaySchedule: params.filters.isTwoDaySchedule,
      });
      if (!response.success || !response.data) {
        showApiError(response);
        return null;
      }
      return { items: response.data.data, totalRecords: response.data.totalRecords };
    },
    [showApiError],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading: isLoading,
    onSearch,
    onSortChange,
    filters,
    setFilters,
    debouncedSearchText,
    flushSearch,
    refetch,
  } = usePaginatedList<Schedule, ScheduleFilters>({
    fetchFn,
    initialFilters: { timeZoneId: null, isTwoDaySchedule: null },
    orderByMap: {
      id: 'scheduleId',
      name: 'name',
      startTime: 'startTime',
      endTime: 'endTime',
      collationMinutes: 'breakMinutes',
      isTwoDays: 'isTwoDaySchedule',
      hasCollationMark: 'hasBreakMarkings',
      timezone: 'timeZoneDescription',
    },
    deduplicate: true,
  });

  // Timezone catalog
  const { options: timezoneOptions } = useCatalogLoader(
    (signal) => timezoneCatalogService.getTimezones(signal),
    (item) => ({ value: String(item.id), label: item.description }),
  );

  // ── Export ─────────────────────────────────────────────────────────────
  const { isExporting, onExport } = useScheduleExport({
    searchText: debouncedSearchText || undefined,
    filters,
  });

  // ── Columns ────────────────────────────────────────────────────────────
  const columns: TableColumn<Schedule>[] = useMemo(() => [
    {
      key: 'id',
      header: translate('schedule:columns.id'),
      sortable: true,
    },
    {
      key: 'name',
      header: translate('schedule:columns.name'),
      sortable: true,
    },
    {
      key: 'startTime',
      header: translate('schedule:columns.startTime'),
      sortable: true,
    },
    {
      key: 'endTime',
      header: translate('schedule:columns.endTime'),
      sortable: true,
    },
    {
      key: 'collationMinutes',
      header: translate('schedule:columns.collationMinutes'),
      sortable: true,
    },
    {
      key: 'isTwoDays',
      header: translate('schedule:columns.isTwoDays'),
      sortable: true,
      render: (row: Schedule) =>
        row.isTwoDays ? translate('schedule:filters.yes') : translate('schedule:filters.no'),
    },
    {
      key: 'hasCollationMark',
      header: translate('schedule:columns.hasCollationMark'),
      sortable: true,
      render: (row: Schedule) =>
        row.hasCollationMark ? translate('schedule:filters.yes') : translate('schedule:filters.no'),
    },
    {
      key: 'timezone',
      header: translate('schedule:columns.timezone'),
      sortable: true,
    },
  ], [translate]);

  // ── Actions ────────────────────────────────────────────────────────────
  const actions: TableAction<Schedule>[] = useMemo(() => [
    {
      id: 'edit',
      label: translate('common:actions.edit'),
      icon: 'edit',
      hidden: () => !canEdit,
      onClick: (row: Schedule) => {
        void navigate(ROUTE_DEFINITIONS.HORARIO_EDITAR.path.replace(':id', String(row.id)));
      },
      color: colors.important.main,
    },
    {
      id: 'delete',
      label: translate('common:actions.delete'),
      icon: 'close',
      hidden: () => !canDelete,
      onClick: (row: Schedule) => {
        openModal('ConfirmModal', {
          variant: 'error',
          icon: 'alert',
          title: translate('schedule:messages.deleteTitle'),
          bodyText: translate('schedule:messages.deleteBody'),
          onConfirm: async () => {
            const response = await scheduleService.delete(row.id);
            if (response.success) {
              showNotification('success', translate('schedule:messages.deleteSuccess'), 3000);
              refetch();
            } else {
              showApiError(response);
            }
          },
        });
      },
      color: colors.feedback.error400,
    },
  ], [
    translate, openModal, showApiError, showNotification,
    refetch, canEdit, canDelete, navigate,
  ]);

  // ── Handlers ───────────────────────────────────────────────────────────
  const applyFilters = useCallback(
    (updater: (previous: ScheduleFilters) => ScheduleFilters) => {
      setFilters(updater);
    },
    [setFilters],
  );

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    actions,
    onSearch,
    onEnterSearch: flushSearch,
    onSortChange,
    isLoading,
    isExporting,
    onExport,
    filters,
    applyFilters,
    timezoneOptions,
  };
}
